/**
 * <category-navigation> -- controls the Categories active-path accordion
 * (snippets/category-navigation.liquid and friends).
 *
 * Server-rendered only: every branch's initial open/closed state (active-path
 * expansion, current-collection highlighting) is already correct in the HTML
 * before this script runs -- it only ever toggles `hidden`/`aria-expanded` on
 * elements that already exist. No taxonomy data lives here, no fetch, no
 * MutationObserver, no polling, no localStorage. Manual accordion toggling is
 * purely client-side UI state; navigating away and back always re-derives
 * expansion fresh from the current page's collection (server-side), never
 * from anything remembered here.
 *
 * One controller instance per <category-navigation> element (desktop sidebar
 * and mobile drawer each get their own, registered once via the standard
 * custom-element definition guard), each with its own scoped click listener
 * -- no global document-level listener.
 *
 * A toggle control is any [aria-controls][aria-expanded] element -- covers
 * both the dedicated chevron button and a group-only node's own label button
 * (which has no Collection URL to navigate to, so its label also toggles the
 * same branch as its chevron). Clicking one:
 *  1. Toggles that branch's own children <ul> hidden/aria-expanded.
 *  2. If the branch is being OPENED, collapses any other already-open
 *     sibling branch at the same level (one open sibling per level) --
 *     ancestors above it and unrelated levels elsewhere are never touched.
 */

if (!customElements.get('category-navigation')) {
  class CategoryNavigation extends HTMLElement {
    connectedCallback() {
      // Guards against double-initialization if this element is ever
      // reconnected to the DOM; each instance still gets its own listener.
      if (this.__pawCategoryNavReady) return;
      this.__pawCategoryNavReady = true;

      this.addEventListener('click', this.#handleClick);
    }

    /** @param {MouseEvent} event */
    #handleClick = (event) => {
      if (!(event.target instanceof Element)) return;

      const toggle = event.target.closest('[aria-controls][aria-expanded]');
      if (toggle) this.#toggleBranch(toggle);
    };

    /** @param {Element} toggle */
    #toggleBranch(toggle) {
      const childrenId = toggle.getAttribute('aria-controls');
      const children = childrenId ? this.querySelector(`#${CSS.escape(childrenId)}`) : null;
      if (!(children instanceof HTMLElement)) return;

      const wasExpanded = toggle.getAttribute('aria-expanded') === 'true';
      const nowExpanded = !wasExpanded;

      this.#setExpanded(toggle, children, nowExpanded);

      if (nowExpanded) this.#collapseSiblings(toggle);
    }

    /**
     * @param {Element} toggle
     * @param {HTMLElement} children
     * @param {boolean} expanded
     */
    #setExpanded(toggle, children, expanded) {
      toggle.setAttribute('aria-expanded', String(expanded));
      children.hidden = !expanded;

      // A group-only node has two toggles sharing the same aria-controls target (its own label
      // button and its dedicated chevron) -- keep both in sync so aria-expanded is never stale
      // on the one that wasn't clicked.
      const childrenId = children.id;
      if (!childrenId) return;
      const row = toggle.closest('.paw-category-row');
      if (!row) return;
      row.querySelectorAll(`[aria-controls="${CSS.escape(childrenId)}"]`).forEach((el) => {
        el.setAttribute('aria-expanded', String(expanded));
      });
    }

    /**
     * Collapses any other already-expanded sibling branch within the same immediate parent
     * <ul> as `toggle`'s own row -- never touches ancestors above this level or unrelated
     * branches elsewhere in the tree.
     * @param {Element} toggle
     */
    #collapseSiblings(toggle) {
      const row = toggle.closest('.paw-category-row');
      const item = row ? row.closest('.paw-category-item') : null;
      const parentList = item ? item.parentElement : null;
      if (!(parentList instanceof HTMLElement)) return;

      const siblingItems = parentList.querySelectorAll(':scope > .paw-category-item');
      siblingItems.forEach((siblingItem) => {
        if (siblingItem === item) return;

        const siblingToggle = siblingItem.querySelector(
          ':scope > .paw-category-row [aria-controls][aria-expanded="true"]'
        );
        if (!siblingToggle) return;

        const siblingChildrenId = siblingToggle.getAttribute('aria-controls');
        const siblingChildren = siblingChildrenId
          ? this.querySelector(`#${CSS.escape(siblingChildrenId)}`)
          : null;
        if (!(siblingChildren instanceof HTMLElement)) return;

        this.#setExpanded(siblingToggle, siblingChildren, false);
      });
    }
  }

  customElements.define('category-navigation', CategoryNavigation);
}
